/**
 * Página de Gerenciamento de Empresas (Tenants)
 * Interface para administradores gerenciarem múltiplas empresas
 * 
 * @author Equipe GestaoeCRM
 * @date 29/09/2025
 */

import { useState, useEffect } from 'react';
import { 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  Building2,
  Mail,
  Phone,
  Users,
  FolderOpen,
  ClipboardList,
  Download,
  CheckCircle,
  XCircle,
  AlertCircle,
  Settings
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant, Tenant } from '@/contexts/TenantContext';
import TenantForm from '@/components/TenantForm';
import { tenantManagementApi, planManagementApi } from '@/services/api';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

export default function TenantsPage() {
  const { user } = useAuth();
  const { 
    tenants, 
    loading, 
    error, 
    fetchTenants, 
    toggleTenantStatus, 
    deleteTenant 
  } = useTenant();

  // Estados locais
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [planFilter, setPlanFilter] = useState<'all' | 'BASIC' | 'PROFESSIONAL' | 'ENTERPRISE'>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [tenantToDelete, setTenantToDelete] = useState<Tenant | null>(null);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [tenantToUpdatePlan, setTenantToUpdatePlan] = useState<Tenant | null>(null);
  const [selectedPlanId, setSelectedPlanId] = useState<string>('');

  const queryClient = useQueryClient();

  // Buscar planos disponíveis
  const { data: plansData } = useQuery({
    queryKey: ['plans'],
    queryFn: async () => {
      const response = await planManagementApi.getAllPlans();
      return response.data;
    },
    enabled: user?.role === 'SUPER_ADMIN'
  });

  // Mutation para atualizar plano
  const updatePlanMutation = useMutation({
    mutationFn: async ({ tenantId, planId }: { tenantId: string; planId: string }) => {
      const response = await tenantManagementApi.updateTenantPlan(tenantId, planId);
      return response.data;
    },
    onSuccess: (data) => {
      toast.success(`Plano atualizado com sucesso! ${data.users.reactivated > 0 ? `${data.users.reactivated} usuários reativados.` : ''} ${data.users.deactivated > 0 ? `${data.users.deactivated} usuários inativados.` : ''}`);
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      setShowPlanModal(false);
      setTenantToUpdatePlan(null);
      setSelectedPlanId('');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Erro ao atualizar plano do tenant');
    }
  });

  // Carregar tenants ao montar o componente
  useEffect(() => {
    if (user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN') {
      fetchTenants();
    }
  }, [user?.role]);

  // Filtrar tenants
  const filteredTenants = tenants.filter(tenant => {
    const matchesSearch = 
      tenant.companyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tenant.tradeName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tenant.cnpj.includes(searchTerm) ||
      tenant.email.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = 
      statusFilter === 'all' || 
      (statusFilter === 'active' && tenant.active) ||
      (statusFilter === 'inactive' && !tenant.active);

    const matchesPlan = 
      planFilter === 'all' || 
      tenant.planType === planFilter;

    return matchesSearch && matchesStatus && matchesPlan;
  });

  // Função para formatar CNPJ
  const formatCNPJ = (cnpj: string) => {
    return cnpj.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  };

  // Função para formatar telefone
  const formatPhone = (phone?: string) => {
    if (!phone) return '-';
    return phone.replace(/^(\d{2})(\d{4,5})(\d{4})$/, '($1) $2-$3');
  };

  // Função para obter cor do plano
  const getPlanColor = (planType: string) => {
    switch (planType) {
      case 'BASIC':
        return 'badge-secondary';
      case 'PROFESSIONAL':
        return 'badge-success';
      case 'ENTERPRISE':
        return 'badge-accent';
      default:
        return 'badge-default';
    }
  };

  // Função para obter nome do plano
  const getPlanName = (planType: string) => {
    switch (planType) {
      case 'FREE':
        return 'Gratuito';
      case 'BASIC':
        return 'Básico';
      case 'PROFESSIONAL':
        return 'Profissional';
      case 'ENTERPRISE':
        return 'Empresarial';
      default:
        return planType;
    }
  };

  // Função para abrir modal de alteração de plano
  const handleOpenPlanModal = (tenant: Tenant) => {
    setTenantToUpdatePlan(tenant);
    // Buscar plano atual do tenant se disponível
    if (tenant.planId) {
      setSelectedPlanId(tenant.planId);
    }
    setShowPlanModal(true);
  };

  // Função para atualizar plano
  const handleUpdatePlan = () => {
    if (!tenantToUpdatePlan || !selectedPlanId) {
      toast.error('Selecione um plano');
      return;
    }

    updatePlanMutation.mutate({
      tenantId: tenantToUpdatePlan.id,
      planId: selectedPlanId
    });
  };

  // Função para alternar status do tenant
  const handleToggleStatus = async (tenant: Tenant) => {
    try {
      await toggleTenantStatus(tenant.id);
      toast.success(`Empresa ${tenant.active ? 'desativada' : 'ativada'} com sucesso!`);
    } catch (error: any) {
      toast.error(error.message || 'Erro ao alterar status da empresa');
    }
  };

  // Função para deletar tenant
  const handleDeleteTenant = async () => {
    if (!tenantToDelete) return;

    try {
      await deleteTenant(tenantToDelete.id);
      toast.success('Empresa deletada com sucesso!');
      setShowDeleteModal(false);
      setTenantToDelete(null);
    } catch (error: any) {
      toast.error(error.message || 'Erro ao deletar empresa');
    }
  };

  // Verificar se usuário é admin ou super admin
  if (user?.role !== 'ADMIN' && user?.role !== 'SUPER_ADMIN') {
    return (
      <div className="space-y-4">
        <div className="text-center py-12">
          <AlertCircle className="mx-auto h-12 w-12 text-text-muted" />
          <h3 className="mt-2 text-sm font-bold text-text-primary">Acesso Negado</h3>
          <p className="mt-1 text-sm text-text-muted">
            Apenas administradores podem acessar o gerenciamento de empresas.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-xl font-semibold leading-6 text-text-primary sm:text-2xl sm:truncate">
            Gerenciar Empresas
          </h2>
          <p className="mt-1 text-sm text-text-muted">
            Gerencie todas as empresas cadastradas no sistema
          </p>
        </div>
        <div className="mt-4 flex md:mt-0 md:ml-4">
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-button shadow-glass text-sm font-bold text-white bg-gradient-to-r from-primary to-secondary hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-offset-background-dark focus:ring-secondary"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nova Empresa
          </button>
        </div>
      </div>

      {/* Filtros e Busca */}
      <div className="bg-background-glass backdrop-blur-glass shadow rounded-glass">
        <div className="px-4 py-5 sm:p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Busca */}
            <div className="md:col-span-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-text-muted" />
                <input
                  type="text"
                  placeholder="Buscar empresas..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 block w-full border-border-glass rounded-button shadow-glass focus:ring-secondary focus:border-secondary/50 sm:text-sm bg-background-glass text-text-primary placeholder-text-muted"
                />
              </div>
            </div>

            {/* Filtro de Status */}
            <div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as any)}
                className="block w-full border-border-glass rounded-button shadow-glass focus:ring-secondary focus:border-secondary/50 sm:text-sm bg-background-glass text-text-primary"
              >
                <option value="all">Todos os Status</option>
                <option value="active">Ativas</option>
                <option value="inactive">Inativas</option>
              </select>
            </div>

            {/* Filtro de Plano */}
            <div>
              <select
                value={planFilter}
                onChange={(e) => setPlanFilter(e.target.value as any)}
                className="block w-full border-border-glass rounded-button shadow-glass focus:ring-secondary focus:border-secondary/50 sm:text-sm bg-background-glass text-text-primary"
              >
                <option value="all">Todos os Planos</option>
                <option value="FREE">Gratuito</option>
                <option value="BASIC">Básico</option>
                <option value="PROFESSIONAL">Profissional</option>
                <option value="ENTERPRISE">Empresarial</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Lista de Tenants */}
      <div className="bg-background-glass backdrop-blur-glass shadow overflow-hidden sm:rounded-button">
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-secondary"></div>
            <p className="mt-2 text-sm text-text-muted">Carregando empresas...</p>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <XCircle className="mx-auto h-12 w-12 text-red-400" />
            <h3 className="mt-2 text-sm font-bold text-text-primary">Erro ao carregar</h3>
            <p className="mt-1 text-sm text-text-muted">{error}</p>
            <button
              onClick={fetchTenants}
              className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-button shadow-glass text-sm font-bold text-white bg-gradient-to-r from-primary to-secondary hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-offset-background-dark focus:ring-secondary"
            >
              Tentar novamente
            </button>
          </div>
        ) : filteredTenants.length === 0 ? (
          <div className="text-center py-12">
            <Building2 className="mx-auto h-12 w-12 text-text-muted" />
            <h3 className="mt-2 text-sm font-bold text-text-primary">Nenhuma empresa encontrada</h3>
            <p className="mt-1 text-sm text-text-muted">
              {searchTerm || statusFilter !== 'all' || planFilter !== 'all'
                ? 'Tente ajustar os filtros de busca.'
                : 'Comece criando uma nova empresa.'}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-border-glass/50">
            {filteredTenants.map((tenant) => (
              <li key={tenant.id} className="px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0">
                        <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center">
                          <Building2 className="h-5 w-5 text-indigo-600" />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2">
                          <p className="text-sm font-bold text-text-primary truncate">
                            {tenant.companyName}
                          </p>
                          {tenant.tradeName && (
                            <span className="text-sm text-text-muted">
                              ({tenant.tradeName})
                            </span>
                          )}
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold ${getPlanColor(tenant.planType)}`}>
                            {getPlanName(tenant.planType)}
                          </span>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold ${
                            tenant.active 
                              ? 'bg-green-500/20 text-green-400' 
                              : 'bg-red-500/20 text-red-400'
                          }`}>
                            {tenant.active ? 'Ativa' : 'Inativa'}
                          </span>
                        </div>
                        <div className="mt-1 flex items-center space-x-4 text-sm text-text-muted">
                          <div className="flex items-center">
                            <span className="font-bold">CNPJ:</span>
                            <span className="ml-1">{formatCNPJ(tenant.cnpj)}</span>
                          </div>
                          <div className="flex items-center">
                            <Mail className="h-4 w-4 mr-1" />
                            <span>{tenant.email}</span>
                          </div>
                          {tenant.phone && (
                            <div className="flex items-center">
                              <Phone className="h-4 w-4 mr-1" />
                              <span>{formatPhone(tenant.phone)}</span>
                            </div>
                          )}
                        </div>
                        <div className="mt-1 flex items-center space-x-4 text-sm text-text-muted">
                          <div className="flex items-center">
                            <Users className="h-4 w-4 mr-1" />
                            <span>{tenant._count?.users || 0} usuários</span>
                          </div>
                          <div className="flex items-center">
                            <FolderOpen className="h-4 w-4 mr-1" />
                            <span>{tenant._count?.projects || 0} projetos</span>
                          </div>
                          <div className="flex items-center">
                            <ClipboardList className="h-4 w-4 mr-1" />
                            <span>{tenant._count?.demands || 0} demandas</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {user?.role === 'SUPER_ADMIN' && (
                      <button
                        onClick={() => handleOpenPlanModal(tenant)}
                        className="text-blue-400 hover:text-blue-900"
                        title="Alterar plano"
                      >
                        <Settings className="h-4 w-4" />
                      </button>
                    )}
                    <button
                      onClick={() => {
                        setSelectedTenant(tenant);
                        setShowEditModal(true);
                      }}
                      className="text-secondary hover:text-primary-900"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleToggleStatus(tenant)}
                      className={`${
                        tenant.active 
                          ? 'text-red-400 hover:text-red-900' 
                          : 'text-green-400 hover:text-green-900'
                      }`}
                    >
                      {tenant.active ? (
                        <XCircle className="h-4 w-4" />
                      ) : (
                        <CheckCircle className="h-4 w-4" />
                      )}
                    </button>
                    <button
                      onClick={() => {
                        setTenantToDelete(tenant);
                        setShowDeleteModal(true);
                      }}
                      className="text-red-400 hover:text-red-900"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Modal de Confirmação de Exclusão */}
      {showDeleteModal && tenantToDelete && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-glass rounded-button bg-background-glass backdrop-blur-glass">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-500/20">
                <Trash2 className="h-6 w-6 text-red-400" />
              </div>
              <h3 className="text-lg font-bold text-text-primary mt-4">
                Confirmar Exclusão
              </h3>
              <div className="mt-2 px-4 py-3">
                <p className="text-sm text-text-muted">
                  Tem certeza que deseja excluir a empresa <strong className="text-text-primary">{tenantToDelete.companyName}</strong>?
                  Esta ação não pode ser desfeita.
                </p>
              </div>
              <div className="flex justify-center space-x-3 px-4 py-3">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setTenantToDelete(null);
                  }}
                  className="px-4 py-2 border border-border-glass rounded-button shadow-glass text-sm font-bold text-text-secondary bg-background-glass backdrop-blur-glass hover:bg-background-glass-hover focus:outline-none focus:ring-2 focus:ring-offset-background-dark focus:ring-secondary"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleDeleteTenant}
                  className="px-4 py-2 border border-transparent rounded-button shadow-glass text-sm font-bold text-white bg-red-500 hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-offset-background-dark focus:ring-red-400"
                >
                  Excluir
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Alteração de Plano (Super Admin) */}
      {showPlanModal && tenantToUpdatePlan && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-glass rounded-button bg-background-glass backdrop-blur-glass">
            <div className="mt-3">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-500/20">
                <Settings className="h-6 w-6 text-blue-400" />
              </div>
              <h3 className="text-lg font-bold text-text-primary mt-4 text-center">
                Alterar Plano
              </h3>
              <div className="mt-2 px-4 py-3">
                <p className="text-sm text-text-muted mb-4">
                  Alterando plano para: <strong className="text-text-primary">{tenantToUpdatePlan.companyName}</strong>
                </p>
                <p className="text-sm text-text-muted mb-4">
                  Plano atual: <strong className="text-text-primary">{getPlanName(tenantToUpdatePlan.planType)}</strong>
                </p>
                <label className="block text-sm font-bold text-text-primary mb-2">
                  Novo Plano
                </label>
                <select
                  value={selectedPlanId}
                  onChange={(e) => setSelectedPlanId(e.target.value)}
                  className="block w-full border-border-glass rounded-button shadow-glass focus:ring-secondary focus:border-secondary/50 sm:text-sm bg-background-glass text-text-primary p-2"
                >
                  <option value="">Selecione um plano</option>
                  {plansData?.map((plan: any) => (
                    <option key={plan.id} value={plan.id}>
                      {plan.name} ({getPlanName(plan.type)}) - {plan.maxUsers} usuários
                    </option>
                  ))}
                </select>
                {selectedPlanId && plansData && (
                  <div className="mt-4 p-3 bg-background-glass-hover rounded-button">
                    {(() => {
                      const selectedPlan = plansData.find((p: any) => p.id === selectedPlanId);
                      if (!selectedPlan) return null;
                      
                      const isChangingToFree = selectedPlan.type === 'FREE';
                      const isChangingFromFree = tenantToUpdatePlan.planType === 'FREE';
                      
                      return (
                        <div className="text-sm text-text-muted">
                          <p className="font-bold text-text-primary mb-2">Ações que serão executadas:</p>
                          {isChangingFromFree && !isChangingToFree ? (
                            <p className="text-green-400">
                              ✓ Usuários inativados por plano serão reativados automaticamente
                            </p>
                          ) : isChangingToFree ? (
                            <p className="text-yellow-400">
                              ⚠ Regras do plano FREE serão aplicadas: usuários excedentes serão inativados conforme limite ({selectedPlan.maxUsers} usuários)
                            </p>
                          ) : (
                            <p className="text-blue-400">
                              ℹ Limites serão ajustados conforme novo plano ({selectedPlan.maxUsers} usuários)
                            </p>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
              <div className="flex justify-center space-x-3 px-4 py-3">
                <button
                  onClick={() => {
                    setShowPlanModal(false);
                    setTenantToUpdatePlan(null);
                    setSelectedPlanId('');
                  }}
                  className="px-4 py-2 border border-border-glass rounded-button shadow-glass text-sm font-bold text-text-secondary bg-background-glass backdrop-blur-glass hover:bg-background-glass-hover focus:outline-none focus:ring-2 focus:ring-offset-background-dark focus:ring-secondary"
                  disabled={updatePlanMutation.isPending}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleUpdatePlan}
                  disabled={!selectedPlanId || updatePlanMutation.isPending}
                  className="px-4 py-2 border border-transparent rounded-button shadow-glass text-sm font-bold text-white bg-gradient-to-r from-primary to-secondary hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-offset-background-dark focus:ring-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {updatePlanMutation.isPending ? 'Atualizando...' : 'Confirmar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Criação/Edição */}
      <TenantForm
        tenant={selectedTenant}
        isOpen={showCreateModal || showEditModal}
        onClose={() => {
          setShowCreateModal(false);
          setShowEditModal(false);
          setSelectedTenant(null);
        }}
        onSuccess={() => {
          fetchTenants();
        }}
      />
    </div>
  );
}
